import apiClient from '../client'

export interface TenantResponse {
  id: string
  code: string
  name: string
  isPlatform: boolean
}

export interface TenantComponentResponse {
  id: string
  componentTypeId: string
  componentTypeName: string | null
  componentTypeCode: string | null
  categoryCode: string | null
  name: string | null
  northing: number
  easting: number
  latitude: number
  longitude: number
  isOutsidePolygon: boolean
}

export interface MyTenantResponse {
  id: string
  code: string
  name: string
  isPlatform: boolean
  ruc: string | null
  legalName: string | null
  commercialName: string | null
  taxAddress: string | null
  ubigeoCode: string | null
  prefix: string | null
  plantBoundaryGeoJson: string | null
  hasPlantBoundary: boolean
  plantUtmZone: number | null
  components: TenantComponentResponse[]
}

export interface VertexRequest {
  northing: number
  easting: number
}

export interface UpdateMyTenantRequest {
  /** 11 digitos. Omitir (undefined) para borrarlo: el backend rechaza la cadena vacia con 400 (longitud minima), no la borra. */
  ruc?: string
  legalName?: string
  commercialName?: string
  prefix?: string
  taxAddress?: string
  ubigeoCode?: string
  plantUtmZone?: number
  /** null/undefined = keep current polygon. Empty array = clear polygon. 3+ vertices = replace. */
  plantBoundaryVertices?: VertexRequest[] | null
}

export interface AddTenantComponentRequest {
  componentTypeId: string
  name?: string
  northing: number
  easting: number
}

export interface UpdateTenantRequest {
  code: string
  name: string
  /** 11 digitos. Omitir (undefined) para borrarlo: el backend rechaza la cadena vacia con 400 (longitud minima), no la borra. */
  ruc?: string
  legalName?: string
  commercialName?: string
  prefix?: string
  taxAddress?: string
  ubigeoCode?: string
}

export interface CreateTenantRequest {
  code: string
  name: string
}

export interface BootstrapTenantAdminRequest {
  username: string
  password: string
  identityDoc: string
  names: string
  paternalSurname: string
  maternalSurname?: string
  email?: string
  companyRuc?: string
  companyLegalName?: string
}

export interface BootstrapTenantAdminResponse {
  tenantId: string
  tenantCode: string
  tenantName: string
  userId: string
  username: string
  personId: string
  companyId: string
  companyRuc: string
  /** Mobile QR onboarding payload: kuntur:setup:v1|{code}|{name}|{username} */
  onboardingPayload: string
}

const BASE_PATH = '/tenants'

export const tenantsService = {
  async list(): Promise<TenantResponse[]> {
    return apiClient.get<TenantResponse[]>(BASE_PATH)
  },

  async create(payload: CreateTenantRequest): Promise<TenantResponse> {
    return apiClient.post<TenantResponse, CreateTenantRequest>(BASE_PATH, payload)
  },

  async bootstrapAdmin(tenantId: string, payload: BootstrapTenantAdminRequest): Promise<BootstrapTenantAdminResponse> {
    return apiClient.post<BootstrapTenantAdminResponse, BootstrapTenantAdminRequest>(
      `${BASE_PATH}/${tenantId}/bootstrap-admin`,
      payload,
    )
  },

  // ── My tenant (Mi Empresa) ──
  async getMine(): Promise<MyTenantResponse> {
    return apiClient.get<MyTenantResponse>(`${BASE_PATH}/me`)
  },

  async updateMine(payload: UpdateMyTenantRequest): Promise<MyTenantResponse> {
    return apiClient.put<MyTenantResponse, UpdateMyTenantRequest>(`${BASE_PATH}/me`, payload)
  },

  async addComponent(payload: AddTenantComponentRequest): Promise<TenantComponentResponse> {
    return apiClient.post<TenantComponentResponse, AddTenantComponentRequest>(
      `${BASE_PATH}/me/components`,
      payload,
    )
  },

  async removeComponent(componentId: string): Promise<void> {
    return apiClient.delete<void>(`${BASE_PATH}/me/components/${componentId}`)
  },

  // ── Admin view (platform-only) ──
  async getById(tenantId: string): Promise<MyTenantResponse> {
    return apiClient.get<MyTenantResponse>(`${BASE_PATH}/${tenantId}`)
  },

  async update(tenantId: string, payload: UpdateTenantRequest): Promise<TenantResponse> {
    return apiClient.put<TenantResponse, UpdateTenantRequest>(`${BASE_PATH}/${tenantId}`, payload)
  },
}
